import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

export interface AvatarResponse {
  userId: number;
  avatar: string;
  createdAt: string;
  updatedAt: string;
}

export interface AvatarUploadRequest {
  userId: number;
  avatarBase64: string;
  createdBy: string;
}

export interface AvatarUpdateRequest {
  avatarBase64: string;
  updatedBy: string;
}

@Injectable({
  providedIn: 'root'
})
export class AvatarService {
  private baseUrl = environment.microservices.user;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  /**
   * Upload inicial de avatar em Base64
   */
  uploadAvatar(request: AvatarUploadRequest): Observable<AvatarResponse> {
    return this.http.post<AvatarResponse>(`${this.baseUrl}/UploadAvatar`, request).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao fazer upload do avatar');
        return throwError(() => err);
      })
    );
  }

  /**
   * Buscar avatar do usuário
   */
  getAvatar(userId: number): Observable<AvatarResponse> {
    return this.http.get<AvatarResponse>(`${this.baseUrl}/GetAvatar/${userId}`).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar avatar');
        return throwError(() => err);
      })
    );
  }

  /**
   * Buscar avatar do usuário, retornando null quando não existir (ex.: 404)
   * sem disparar notificação de erro para esse caso.
   */
  getAvatarOptional(userId: number): Observable<AvatarResponse | null> {
    return this.http.get<AvatarResponse>(`${this.baseUrl}/GetAvatar/${userId}`).pipe(
      catchError((err: any) => {
        if (err?.status === 404) {
          return of(null);
        }
        this.errorService.handleHttpError(err, 'Erro ao buscar avatar');
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualizar avatar existente
   */
  updateAvatar(userId: number, request: AvatarUpdateRequest): Observable<AvatarResponse> {
    return this.http.put<AvatarResponse>(`${this.baseUrl}/UpdateAvatar/${userId}`, request).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao atualizar avatar');
        return throwError(() => err);
      })
    );
  }

  /**
   * Converte arquivo para Base64
   */
  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove o prefixo "data:image/...;base64," se existir
        const base64String = reader.result as string;
        const base64Data = base64String.split(',')[1] || base64String;
        resolve(base64Data);
      };
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Valida se o arquivo é uma imagem válida
   */
  validateImageFile(file: File): { valid: boolean; error?: string } {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Tipo de arquivo inválido. Apenas JPEG, PNG e GIF são permitidos.'
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'Arquivo muito grande. O tamanho máximo é 5MB.'
      };
    }

    return { valid: true };
  }
}
